import React from 'react';
import { useSearchParams } from 'react-router-dom';
import './PolicyPage.css';

// 개인정보처리방침 버전 관리
// V10: 2026-11-02 시행 (내 자료함 자동 모으기·지난 대화 찾아보기, 채팅 첨부 보유기간, 생기부 근거 보기)
// V9: 2026-10-21 시행 (교육 뉴스레터 구독, 이메일 발송 국외 위탁 Resend)
// V8: 2026-10-19 시행 (학생 메모·임원·진로희망)
// V7: 2026-10-18 시행 (학급 명단·서술형 채점·과제 제출방 학생 정보, 클라우드 서버·저장소 위치)
// V6: 2026-08-31 시행 (자율연수 강사 계약·겸직·정산 자료)
// V5: 2026-08-07 시행 (AI 맞춤 설정·저장된 기억·대화 요약)
// V4: 2026-07-29 시행 (학교생활기록 목적 제한, 개인정보 마스킹·원본 파일 미보관)
// V3: 2026-01-28 시행 (만14세 미만 아동 보호, 파기 절차, 쿠키 등 대폭 강화)
// V2: 2025-12-18 시행 (AI 국외 이전 명시)
// V1: 2025-01-15 시행 (최초 버전)
const POLICY_V3_EFFECTIVE_DATE = new Date('2026-01-28');
const POLICY_V2_EFFECTIVE_DATE = new Date('2025-12-18');
const POLICY_V5_EFFECTIVE_DATE = new Date('2026-08-07T00:00:00+09:00');
const POLICY_V6_EFFECTIVE_DATE = new Date('2026-08-31T00:00:00+09:00');
const POLICY_V7_EFFECTIVE_DATE = new Date('2026-10-18T00:00:00+09:00');
const POLICY_V8_EFFECTIVE_DATE = new Date('2026-10-19T00:00:00+09:00');
const POLICY_V9_EFFECTIVE_DATE = new Date('2026-10-21T00:00:00+09:00');
const POLICY_V10_EFFECTIVE_DATE = new Date('2026-11-02T00:00:00+09:00');
const POLICY_V10_REVISED_LABEL = '2026년 11월 2일';

const PrivacyPolicy = () => {
  const [searchParams] = useSearchParams();
  const now = new Date();
  
  // ?preview=v10 또는 ?preview=new 쿼리 파라미터로 다음 개정 미리보기 가능
  const previewVersion = searchParams.get('preview');
  const isPreviewV10 = previewVersion === 'v10' || previewVersion === 'new';
  const isPreviewV9 = previewVersion === 'v9';
  const isPreviewV8 = previewVersion === 'v8';
  const isPreviewV7 = previewVersion === 'v7';
  const isPreviewV6 = previewVersion === 'v6';
  const isPreviewV5 = previewVersion === 'v5';
  const isPreviewV3 = previewVersion === 'v3';
  const isPreviewV2 = previewVersion === 'v2';
  const policyV10Enabled = process.env.REACT_APP_PRIVACY_V10_ENABLED === 'true';
  
  // 버전 결정 로직
  const olderPreview = isPreviewV7 || isPreviewV6 || isPreviewV5 || isPreviewV3 || isPreviewV2;
  const v10Live = policyV10Enabled || now >= POLICY_V10_EFFECTIVE_DATE;
  if (isPreviewV10 || (v10Live && !isPreviewV9 && !isPreviewV8 && !olderPreview)) {
    return (
      <PrivacyPolicyV3
        isPreview={isPreviewV10}
        partnerPolicyEnabled
        personalizationPolicyEnabled
        studentDataPolicyEnabled
        studentNotesPolicyEnabled
        newsletterPolicyEnabled
        aiReferencePolicyEnabled
      />
    );
  }
  if (isPreviewV9 || (!isPreviewV8 && !olderPreview && now >= POLICY_V9_EFFECTIVE_DATE)) {
    return (
      <PrivacyPolicyV3
        isPreview={isPreviewV9}
        partnerPolicyEnabled
        personalizationPolicyEnabled
        studentDataPolicyEnabled
        studentNotesPolicyEnabled
        newsletterPolicyEnabled
      />
    );
  }
  if (isPreviewV8 || (!olderPreview && now >= POLICY_V8_EFFECTIVE_DATE)) {
    return (
      <PrivacyPolicyV3
        isPreview={isPreviewV8}
        partnerPolicyEnabled
        personalizationPolicyEnabled
        studentDataPolicyEnabled
        studentNotesPolicyEnabled
      />
    );
  }
  if (isPreviewV7 || (!isPreviewV6 && now >= POLICY_V7_EFFECTIVE_DATE)) {
    return (
      <PrivacyPolicyV3
        isPreview={isPreviewV7}
        partnerPolicyEnabled
        personalizationPolicyEnabled
        studentDataPolicyEnabled
      />
    );
  }
  if (isPreviewV6 || now >= POLICY_V6_EFFECTIVE_DATE) {
    return (
      <PrivacyPolicyV3
        isPreview={isPreviewV6}
        partnerPolicyEnabled
        personalizationPolicyEnabled
      />
    );
  }
  if (isPreviewV5 || now >= POLICY_V5_EFFECTIVE_DATE) {
    return (
      <PrivacyPolicyV3
        isPreview={isPreviewV5}
        partnerPolicyEnabled={false}
        personalizationPolicyEnabled
      />
    );
  }
  if (isPreviewV3 || now >= POLICY_V3_EFFECTIVE_DATE) {
    return (
      <PrivacyPolicyV3
        isPreview={isPreviewV3}
        partnerPolicyEnabled={false}
        personalizationPolicyEnabled={false}
      />
    );
  } else if (isPreviewV2 || now >= POLICY_V2_EFFECTIVE_DATE) {
    return <PrivacyPolicyV2 isPreview={isPreviewV2} />;
  } else {
    return <PrivacyPolicyV1 />;
  }
};

// ============================================================
// V3 개인정보처리방침 (2026-01-28 이후 적용)
// 만14세 미만 아동 보호, 파기 절차, 쿠키, 수집 방법 등 대폭 강화
// ============================================================
const PolicySection = ({ title, children }) => (
  <section className="policy-section">
    <h2>{title}</h2>
    {children}
  </section>
);

const PolicyFooterV3 = () => (
  <nav className="policy-related" aria-label="관련 정책">
    <div className="policy-related-links">
      <a className="policy-link" href="/company-info">회사정보</a>
      <a className="policy-link" href="/terms-of-service">이용약관</a>
      <a className="policy-link" href="/refund-policy">환불정책</a>
    </div>
  </nav>
);

const PrivacyPolicyV3 = ({
  isPreview = false,
  partnerPolicyEnabled = false,
  personalizationPolicyEnabled = false,
  studentDataPolicyEnabled = false,
  studentNotesPolicyEnabled = false,
  newsletterPolicyEnabled = false,
  aiReferencePolicyEnabled = false,
}) => {
  const lastRevised = aiReferencePolicyEnabled
    ? POLICY_V10_REVISED_LABEL
    : newsletterPolicyEnabled
    ? '2026년 10월 21일'
    : studentNotesPolicyEnabled
    ? '2026년 10월 19일'
    : studentDataPolicyEnabled
    ? '2026년 10월 18일'
    : partnerPolicyEnabled ? '2026년 8월 31일' : personalizationPolicyEnabled ? '2026년 8월 7일' : '2026년 7월 15일';
  const previewNotice = aiReferencePolicyEnabled
    ? `미리보기 모드 - 내 자료함 자동 모으기·지난 대화 찾아보기 관련 개정은 ${POLICY_V10_REVISED_LABEL}부터 적용됩니다`
    : newsletterPolicyEnabled
    ? '미리보기 모드 - 교육 뉴스레터 구독·이메일 발송 위탁 관련 개정은 2026년 10월 21일부터 적용됩니다'
    : studentNotesPolicyEnabled
    ? '미리보기 모드 - 학생 메모·임원·진로희망 관련 개정은 2026년 10월 19일부터 적용됩니다'
    : studentDataPolicyEnabled
    ? '미리보기 모드 - 학급 명단·서술형 채점·과제 제출방 학생 정보 관련 개정은 2026년 10월 18일부터 적용됩니다'
    : '미리보기 모드 - 자율연수 강사 계약 관련 개정은 2026년 8월 31일부터 적용됩니다';
  return (
    <div className="policy-page">
      <main className="policy-document">
        <header className="policy-header">
          <h1 className="policy-title">개인정보처리방침</h1>
        </header>

        {/* 미리보기 모드 배너 */}
        {isPreview && (
          <div className="policy-preview">
            {previewNotice}
          </div>
        )}

        <div className="policy-effective">
          시행일: 2026년 1월 28일 | 최종 개정일: {lastRevised}
        </div>

        <p className="policy-intro">
          플랙티컬 주식회사(이하 "회사")는 「개인정보 보호법」 제30조에 따라 정보주체의 개인정보를 보호하고 이와 관련한 고충을 신속하고 원활하게 처리할 수 있도록 하기 위하여 다음과 같이 개인정보처리방침을 수립·공개합니다. 회사는 이용자의 개인정보를 처리함에 있어서 「개인정보 보호법」, 「정보통신망 이용촉진 및 정보보호 등에 관한 법률」 및 관련 법령을 준수하고 있습니다.
        </p>

        <PolicySection title="제1조 (수집하는 개인정보의 항목 및 수집방법)">
          <h3>① 회사는 다음과 같은 개인정보를 수집하고 있습니다.</h3>
          
          <div className="policy-card policy-card--info">
            <h3>1. 회원 가입 및 관리</h3>
            <ul>
              <li><strong>필수 항목:</strong> 이메일 주소(회원 ID)</li>
              <li><strong>Google 계정 연동 시:</strong> 이메일 주소, 이름, 프로필 사진</li>
            </ul>
          </div>

          <div className="policy-card">
            <h3>2. 서비스 이용 과정에서 자동으로 수집되는 정보</h3>
            <ul>
              <li>IP 주소, 서비스 이용기록, 방문기록</li>
              <li>회원가입 일시, 접속 로그</li>
              <li>기기정보 (디바이스 유형, 브라우저 종류)</li>
              <li>쿠키 정보</li>
            </ul>
          </div>

          <div className="policy-card">
            <h3>3. 유료 서비스 이용 시</h3>
            <ul>
              <li><strong>결제정보:</strong> 결제수단 정보 (토스페이먼츠를 통해 처리)</li>
              <li>구매 및 결제 기록</li>
            </ul>
          </div>

          <div className="policy-card">
            <h3>4. AI 서비스 이용 시</h3>
            <ul>
              <li>입력 텍스트(프롬프트)</li>
              <li>첨부 파일 내용</li>
              <li>AI 이용 기록</li>
              {personalizationPolicyEnabled && (
                <>
                  <li>이용자가 입력한 교사 업무 정보(선택 입력한 학교 이름, 학교급, 담당 학년·과목·역할, 주 사용 업무) 및 AI 맞춤 설정</li>
                  <li>대화에서 이용자가 직접 저장을 요청하거나 장기간 유용하다고 판단되어 저장된 답변 선호·업무 맥락(이하 "AI 기억")</li>
                  <li>동일한 대화를 이어가기 위해 개인정보를 가리고 구조화한 대화 요약</li>
                </>
              )}
              {aiReferencePolicyEnabled && (
                <>
                  <li>(이용자가 허용한 경우) 채팅에 올린 문서 파일의 이름과 추출한 본문을 내 자료함에 모아 둔 사본</li>
                  <li>(이용자가 허용한 경우) 지난 대화를 찾아보기 위해 학생 이름 등 개인정보를 가리고 만든 대화 검색용 요약</li>
                </>
              )}
              <li>생기부 검사 시 학생 이름 및 학교생활기록 문장</li>
              {aiReferencePolicyEnabled && (
                <li>생기부 생성 시 입력한 내용과 첨부 파일에서 읽은 글(연락처 등 개인정보를 가린 형태, 생성 결과의 근거 보기용)</li>
              )}
            </ul>
          </div>

          <div className="policy-card">
            <h3>5. 고객 문의 시</h3>
            <ul>
              <li><strong>필수 항목:</strong> 이메일 주소, 문의 내용</li>
              <li><strong>선택 항목:</strong> 성명(이름)</li>
            </ul>
          </div>

          {partnerPolicyEnabled && (
            <div className="policy-card">
              <h3>6. 온라인 자율연수 강사 계약·정산 시</h3>
              <ul>
                <li><strong>필수 항목:</strong> 성명, 이메일, 연락처, 소속기관</li>
                <li><strong>계약·복무 자료:</strong> 전자서명, 최종 계약서, 겸직허가 또는 불필요 서면확인, 확인기관·확인일·유효기간</li>
                <li><strong>정산 자료:</strong> 판매·환불·인세·원천징수 내역, 세무 신고에 필요한 정보</li>
                <li>주민등록번호와 계좌정보는 일반 이메일이나 공개 화면이 아닌 별도 최소권한 경로로 수집합니다.</li>
              </ul>
            </div>
          )}

          {studentDataPolicyEnabled && (
            <div className="policy-card">
              <h3>7. 학급 관리·서술형 채점·과제 제출방 이용 시 (학생 정보)</h3>
              <ul>
                <li><strong>학급 명단:</strong> 학생 이름, 번호, 이용자가 기록에서 고친 이름 표기(이하 "이름 별칭"), 명단 이름(예: 2학년 3반), 학년도·학기</li>
                {studentNotesPolicyEnabled && (
                  <li><strong>학생 메모:</strong> 이용자가 학생별로 남긴 관찰 메모와 작성일·영역, 임원(역할·기간)과 진로희망</li>
                )}
                <li><strong>서술형 채점:</strong> 학생 이름, 답안 파일과 AI가 읽은 답안 글, 점수·피드백</li>
                <li><strong>과제 제출방:</strong> 학생이 입력한 이름, 제출 파일, 제출 일시</li>
                <li>회사는 학생의 학번, 생년월일, 연락처, 성별, 법정대리인 정보를 따로 요구하지 않습니다.</li>
              </ul>
            </div>
          )}

          {newsletterPolicyEnabled && (
            <div className="policy-card">
              <h3>8. 교육 뉴스레터 구독 시 (회원 가입 없이도 가능)</h3>
              <ul>
                <li><strong>필수 항목:</strong> 이메일 주소</li>
                <li><strong>동의 기록:</strong> 동의 일시, 동의한 문구와 버전, 신청할 때의 IP 주소와 브라우저 정보(수신동의를 증명하기 위한 기록)</li>
                <li><strong>로그인 상태에서 신청한 경우:</strong> 회원 식별값(로그인 이메일이 인증된 계정이면 확인 메일 없이 바로 구독)</li>
                <li><strong>발송 기록:</strong> 마지막으로 보낸 날짜, 수신동의 재확인 안내를 보낸 일시</li>
              </ul>
            </div>
          )}

          <h3>② 회사는 다음과 같은 방법으로 개인정보를 수집합니다.</h3>
          <ul>
            <li>홈페이지, 애플리케이션 또는 서비스 이용 과정에서 이용자가 직접 입력하는 방식</li>
            <li>자동 수집 장치(쿠키 등)를 통한 생성정보의 수집</li>
            <li>고객센터를 통한 상담과정에서 이메일 등을 통해 수집하는 방식</li>
            {studentDataPolicyEnabled && (
              <>
                <li>이용자가 채점·생기부 기록에서 학생 이름을 고칠 때 이름 별칭이 학급 명단에 자동으로 저장되는 방식</li>
                <li>이용자(교사)가 공유한 과제 제출 링크에서 학생이 로그인 없이 이름과 과제를 직접 제출하는 방식</li>
              </>
            )}
            {newsletterPolicyEnabled && (
              <li>교육 뉴스 페이지의 '뉴스레터로 받기'에서 이용자가 이메일 주소를 직접 입력하고 동의하는 방식</li>
            )}
          </ul>
        </PolicySection>

        <PolicySection title="제2조 (개인정보의 처리 목적)">
          <p>회사는 수집한 개인정보를 다음의 목적으로 처리합니다. 처리하고 있는 개인정보는 다음의 목적 이외의 용도로는 이용되지 않으며, 이용 목적이 변경되는 경우에는 「개인정보 보호법」 제18조에 따라 별도의 동의를 받는 등 필요한 조치를 이행합니다.</p>
          
          <ol>
            <li>
              <strong>회원 가입 및 관리</strong>
              <ul>
                <li>회원 가입 의사 확인, 서비스 제공에 따른 본인 식별·인증</li>
                <li>회원자격 유지·관리, 중복가입 확인</li>
                <li>불량이용자 관리, 비정상 사용자 적발 및 이용제한</li>
                <li>분쟁처리, 고지사항 전달</li>
              </ul>
            </li>
            <li>
              <strong>서비스 제공</strong>
              <ul>
                <li>AI 기반 기능(생기부 생성·검수, AI 상담, 퀴즈·학습지 생성 등) 제공</li>
                {personalizationPolicyEnabled && (
                  <li>이용자가 같은 설명을 반복하지 않도록 AI 답변을 개인화하고 장기 대화의 맥락을 유지</li>
                )}
                {aiReferencePolicyEnabled && (
                  <li>이용자가 허용한 경우, 채팅에 올린 파일과 지난 대화에서 지금 요청에 필요한 내용을 찾아 AI 답변에 참고</li>
                )}
                {studentDataPolicyEnabled && (
                  <li>이용자(교사)의 학생 지도·평가 업무 지원: 학급 명단으로 학생 이름 맞추기, 서술형 채점과 결과 다시보기, 과제 제출 현황 확인, 학생별 기록 모아보기와 이를 근거로 한 생활기록부 초안 작성(이용자가 요청한 경우)</li>
                )}
                <li>콘텐츠 제공, 요금결제·정산</li>
              </ul>
            </li>
            <li>
              <strong>고충처리</strong>
              <ul>
                <li>불만처리 및 고객서비스 제공</li>
                <li>민원인의 신원 확인, 민원사항 확인</li>
                <li>사실조사를 위한 연락·통지, 처리결과 통보</li>
              </ul>
            </li>
            {partnerPolicyEnabled && (
              <li>
                <strong>온라인 자율연수 강사 계약·정산</strong>
                <ul>
                  <li>강사 본인 확인, 계약 체결·이행 및 겸직·복무 확인</li>
                  <li>판매·환불 내역에 따른 인세 산정, 원천징수와 지급명세서 제출</li>
                  <li>계약·승인 유효기간 확인과 판매 자격 관리</li>
                </ul>
              </li>
            )}
            {newsletterPolicyEnabled && (
              <li>
                <strong>교육 뉴스레터 발송 (이용자가 동의한 경우에만)</strong>
                <ul>
                  <li>매일 아침 교육·에듀테크 뉴스와 왓퀴즈 새 기능·이벤트 소식(영리목적 광고성 정보) 발송</li>
                  <li>구독 확인 메일, 수신동의·수신거부 처리 결과 안내, 2년마다 수신동의 여부 확인 안내</li>
                  <li>수신동의 사실의 증명과 같은 주소로 반복되는 부정 신청 방지</li>
                </ul>
              </li>
            )}
          </ol>
        </PolicySection>

        <PolicySection title="제3조 (개인정보의 보유기간 및 파기)">
          <p>① 회사는 이용자로부터 수집한 개인정보를 이용자 자격이 유지되는 동안 보유 및 이용하며, 이용자 자격이 상실되는 경우 또는 해당 정보의 이용 목적이 달성된 경우에는 지체 없이 파기합니다.</p>
          
          <h3>② 다만, 다음의 경우에는 명시한 기간 동안 보관합니다:</h3>
          
          <div className="policy-table-wrap">
            <table className="policy-table">
              <thead>
                <tr>
                  <th scope="col">보유 항목</th>
                  <th scope="col">보유 기간</th>
                  <th scope="col">근거 법령</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>회원 탈퇴 후 재가입 제한, 분쟁 해결</td>
                  <td>30일</td>
                  <td>내부 방침</td>
                </tr>
                <tr>
                  <td>생기부 검사 기록(개인정보 마스킹된 검사 초안, 점검 항목, AI 수정 제안, 학생 이름)</td>
                  <td>이용자가 직접 삭제하거나 회원 탈퇴 시까지</td>
                  <td>검사 결과 다시보기 제공을 위한 내부 방침</td>
                </tr>
                {studentDataPolicyEnabled && (
                  <>
                    <tr>
                      <td>학급 명단(학생 이름, 번호, 이름 별칭)</td>
                      <td>이용자가 직접 삭제하거나 회원 탈퇴 시까지. 지난 학년도 명단은 이름 맞추기에 쓰지 않음</td>
                      <td>이름 맞추기 제공을 위한 내부 방침</td>
                    </tr>
                    {studentNotesPolicyEnabled && (
                      <tr>
                        <td>학생 메모(관찰 메모, 임원·진로희망)</td>
                        <td>이용자가 직접 삭제하거나 회원 탈퇴 시까지. 명단을 지우면 남은 명단에 없는 학생의 메모도 함께 삭제</td>
                        <td>학생별 기록 모아보기와 생활기록부 초안 작성 지원을 위한 내부 방침</td>
                      </tr>
                    )}
                    <tr>
                      <td>서술형 채점 기록(학생 이름, 답안 파일, AI가 읽은 답안 글, 점수·피드백)</td>
                      <td>이용자가 기록을 삭제하거나 회원 탈퇴 시까지</td>
                      <td>채점 결과 다시보기와 채점 근거 확인 제공을 위한 내부 방침</td>
                    </tr>
                    <tr>
                      <td>과제 제출방 제출물(학생 이름, 제출 파일)</td>
                      <td>이용자가 제출물 또는 제출방을 삭제하거나 회원 탈퇴 시까지</td>
                      <td>제출 현황 확인과 채점 제공을 위한 내부 방침</td>
                    </tr>
                    <tr>
                      <td>채점·생기부 생성을 위해 올린 처리용 임시 파일</td>
                      <td>서술형 채점은 1일, 생기부 생성은 7일이 지나면 자동 파기</td>
                      <td>요청한 처리를 마치기 위한 내부 방침</td>
                    </tr>
                  </>
                )}
                {newsletterPolicyEnabled && (
                  <tr>
                    <td>교육 뉴스레터 구독 정보(이메일 주소, 동의 기록, 발송 기록)</td>
                    <td>수신거부하면 즉시 파기. 확인 메일의 '구독 확정하기'를 7일 안에 누르지 않은 신청은 그 뒤 자동 파기. 회원 탈퇴와 관계없이 수신거부할 때까지 보관</td>
                    <td>이용자 동의(「정보통신망법」 제50조)</td>
                  </tr>
                )}
                {personalizationPolicyEnabled && (
                  <tr>
                    <td>교사 업무 정보 및 AI 기억</td>
                    <td>이용자가 마이페이지에서 직접 삭제하거나 회원 탈퇴 시까지</td>
                    <td>AI 맞춤 답변 제공을 위한 이용자 설정</td>
                  </tr>
                )}
                {aiReferencePolicyEnabled && (
                  <>
                    <tr>
                      <td>생기부 근거 보기 자료(생성할 때 입력한 내용과 첨부 파일에서 읽은 글, 개인정보를 가린 형태. 원본 파일은 보관하지 않음)</td>
                      <td>이용자가 생기부 기록을 삭제하거나 회원 탈퇴 시까지</td>
                      <td>생성 결과의 근거 확인과 문장 고치기 제공을 위한 내부 방침</td>
                    </tr>
                    <tr>
                      <td>채팅에 올린 첨부 파일(원본과 추출한 본문)</td>
                      <td>그 대화를 삭제하거나 회원 탈퇴 시까지</td>
                      <td>대화 이어가기와 파일 다시 열기를 위한 내부 방침</td>
                    </tr>
                    <tr>
                      <td>내 자료함(직접 저장한 자료와 채팅에서 자동으로 모은 파일 사본)</td>
                      <td>이용자가 직접 삭제하거나 회원 탈퇴 시까지. 무료 이용자가 자동으로 모은 파일은 최근 20개만 남기고 오래된 것부터 지움</td>
                      <td>이용자 동의 및 설정</td>
                    </tr>
                    <tr>
                      <td>지난 대화 검색용 요약(개인정보를 가린 형태)</td>
                      <td>원래 대화를 삭제하거나 회원 탈퇴 시까지</td>
                      <td>이용자 동의 및 설정</td>
                    </tr>
                  </>
                )}
                {partnerPolicyEnabled && (
                  <tr>
                    <td>자율연수 강사 계약·겸직 확인·정산 및 세무 기록</td>
                    <td>계약 종료 또는 최종 정산 후 5년</td>
                    <td>계약·세무 분쟁 대응을 위한 내부 방침 및 관계 세법</td>
                  </tr>
                )}
                <tr>
                  <td>표시·광고에 관한 기록</td>
                  <td>6개월</td>
                  <td>전자상거래법</td>
                </tr>
                <tr>
                  <td>계약 또는 청약 철회에 관한 기록</td>
                  <td>5년</td>
                  <td>전자상거래법</td>
                </tr>
                <tr>
                  <td>대금결제 및 재화 등의 공급에 관한 기록</td>
                  <td>5년</td>
                  <td>전자상거래법</td>
                </tr>
                <tr>
                  <td>소비자의 불만 또는 분쟁처리에 관한 기록</td>
                  <td>3년</td>
                  <td>전자상거래법</td>
                </tr>
                <tr>
                  <td>접속 로그, 접속 IP 정보</td>
                  <td>3개월</td>
                  <td>통신비밀보호법</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>③ 개인정보 파기 절차 및 방법</h3>
          <ul>
            <li><strong>파기 절차:</strong> 이용자가 회원가입 등을 위해 입력한 정보는 목적이 달성된 후 별도의 DB로 옮겨져 내부 방침 및 기타 관련 법령에 의한 정보보호 사유에 따라 일정 기간 저장된 후 파기됩니다.</li>
            <li><strong>파기 방법:</strong> 전자적 파일 형태로 저장된 개인정보는 기록을 재생할 수 없는 기술적 방법을 사용하여 삭제합니다. 종이에 출력된 개인정보는 분쇄기로 분쇄하여 파기합니다.</li>
          </ul>

          <p>④ 이용자 탈퇴를 신청하시면 해당 계정은 즉시 탈퇴 처리되며, 계정에 연결된 생기부 생성 기록, 검사 기록 및 검사 사용 기록{studentDataPolicyEnabled ? `, 학급 명단${studentNotesPolicyEnabled ? ', 학생 메모' : ''}, 서술형 채점 기록과 답안 파일, 과제 제출방과 제출물` : ''}도 함께 삭제합니다.</p>
        </PolicySection>

        <PolicySection title="제3조의2 (학교생활기록 처리에 관한 특칙)">
          <p>① 회사는 생기부 생성·검사 기능에서 처리하는 학교생활기록 및 관련 자료를 <strong>담당 교사의 학생 지도, 기록 작성, 기재요령 검사 및 이용자가 요청한 결과 제공</strong>을 위한 범위에서만 처리합니다.</p>
          <p>② 회사는 학교생활기록 및 검사 자료를 입시 상담, 합격 예측, 유료 강의·자료 제작, 수강생 모집, 광고, 제3자 판매 또는 회사·AI 제공사의 모델 학습 목적으로 이용하지 않습니다.</p>
          <p>③ 연락처 등 개인정보는 AI 요청 전에 자동으로 마스킹하여 처리합니다.</p>
          <p>④ 검사에 업로드한 원본 파일 자체는 요청한 검사를 수행하는 동안에만 처리하며 별도 파일로 보관하지 않습니다. 작성 중인 검사 초안은 연락처 등 개인정보를 가린 형태로 기록될 수 있고, 이용자는 언제든 해당 기록을 직접 삭제할 수 있습니다.</p>
          <p>⑤ 회원은 최종 확정된 학교생활기록부나 공식 발급본이 아닌 작성 중인 초안만 검사기에 입력해야 합니다.</p>
          <p>⑥ 회사는 「초·중등교육법」 제25조의2에 따라 학교생활기록을 영업 목적으로 거래하거나 이용하지 않으며, 회원이 해당 목적으로 서비스를 이용하는 것을 금지합니다.</p>
          {aiReferencePolicyEnabled && (
            <p>⑦ 생기부 생성에서 이용자가 입력한 내용과 첨부 파일에서 읽은 글은 연락처 등 개인정보를 가린 형태로 생성 기록과 함께 보관하며, 생성 결과의 각 문장이 어느 입력에서 나왔는지 보여주는 '근거 보기'와 이용자가 요청한 문장 고치기에만 사용합니다. 근거를 찾을 때에도 개인정보를 가린 글만 AI 제공사에 보냅니다. 첨부한 원본 파일 자체는 보관하지 않으며, 이용자가 생기부 기록을 삭제하거나 회원 탈퇴하면 함께 삭제합니다.</p>
          )}
        </PolicySection>

        {personalizationPolicyEnabled && (
          <PolicySection title="제3조의3 (AI 맞춤 설정 및 기억에 관한 특칙)">
            <p>① AI 기억은 대화 기록과 별도로 저장될 수 있습니다. 따라서 특정 대화를 삭제하더라도 그 대화에서 별도로 저장된 AI 기억은 자동으로 삭제되지 않을 수 있으며, 이용자는 마이페이지의 AI 맞춤 설정에서 개별 기억 또는 전체 기억을 언제든 확인·수정·삭제할 수 있습니다.</p>
            <p>② 이용자는 AI 기억 기능을 언제든 끌 수 있습니다. 기능을 끄면 저장된 기억과 교사 업무 정보를 새로운 AI 답변에 사용하지 않으며, 기존 기억은 이용자가 다시 켜거나 직접 삭제할 때까지 보관됩니다.</p>
            <p>③ 회사는 학생 이름, 학번, 성적, 진단, 상담 내용, 가정환경 및 이용자가 번역·요약·수정을 위해 제공한 학생 자료를 AI 기억으로 자동 저장하지 않습니다.</p>
            <p>④ AI 기억은 현재 요청과 관련된 범위에서만 사용되며, 현재 대화에서 이용자가 새로 제공한 정보와 충돌할 경우 현재 대화의 정보를 우선합니다. 학교 이름은 이용자가 직접 입력한 경우에 한해 행정문서·가정통신문 등 기관명 자리가 필요한 요청에서만 사용합니다.</p>
          </PolicySection>
        )}

        {studentDataPolicyEnabled && (
          <PolicySection title="제3조의4 (학생 정보 처리에 관한 특칙)">
            <p>① 회사는 학급 명단, 서술형 채점, 과제 제출방에서 처리하는 학생 정보를 <strong>이용자(교사)가 요청한 학생 지도·평가·기록 작성 업무를 위한 범위에서만</strong> 이용자를 위하여 처리합니다.</p>
            <p>② 학급 명단의 학생 이름은 올린 자료의 학생 이름 맞추기와 과제 제출 현황 확인에만 사용합니다. 명단 대조는 회사 서버 안에서 처리하며, 학급 명단을 AI 제공사에 보내지 않습니다.</p>
            <p>③ 학급 명단과 제출 현황은 명단을 만든 이용자 본인만 볼 수 있습니다. 과제 제출방의 학생 화면에는 학급 명단이나 다른 학생의 제출 여부를 보여주지 않습니다.</p>
            <p>④ 회사는 학생 정보를 광고·마케팅, 입시 상담, 제3자 판매 또는 회사·AI 제공사의 모델 학습 목적으로 이용하지 않습니다.</p>
            <p>⑤ 이용자는 학급 관리 화면에서 학급 명단과 이름 별칭을, 각 기록 화면에서 채점 기록과 제출물을 언제든 확인·수정·삭제할 수 있습니다. 학생 또는 법정대리인은 담당 교사(학교)를 통하거나 제12조의 개인정보보호책임자에게 열람·정정·삭제·처리정지를 요청할 수 있으며, 회사는 해당 이용자에게 알리고 지체 없이 조치합니다.</p>
            <p>⑥ 이용자는 학생의 건강·장애·가정환경 등 민감정보나 주민등록번호 등 고유식별정보를 학급 명단, 과제 안내, 제출방 이름 칸에 입력하지 않아야 합니다.</p>
            <p>⑦ 학교가 이 서비스를 학생 개인정보를 처리하는 학습지원 소프트웨어로 선정하려는 경우(「초·중등교육법」 제29조의2), 회사는 선정 기준 확인에 필요한 개인정보 보호 자료를 제공합니다.</p>
            {studentNotesPolicyEnabled && (
              <p>⑧ 학생 메모와 임원·진로희망은 이용자 본인만 볼 수 있습니다. 이용자가 '이 학생 생기부 쓰기'로 생활기록부 초안 작성에 넣은 경우에만 입력 텍스트로 AI 제공사에 보내며, 그 밖에는 AI 제공사에 보내지 않습니다. 이용자는 학생 화면에서 메모를 언제든 고치거나 지울 수 있고, 학생의 건강·장애·가정환경 등 민감정보는 메모에 적지 않아야 합니다.</p>
            )}
          </PolicySection>
        )}

        {aiReferencePolicyEnabled && (
          <PolicySection title="제3조의5 (내 자료함 자동 모으기와 지난 대화 찾아보기에 관한 특칙)">
            <p>① 두 기능은 이용자가 채팅 화면의 안내 또는 AI 맞춤 설정에서 허용한 경우에만 동작하며, 이용자는 AI 맞춤 설정에서 각각 언제든 끌 수 있습니다. 끄면 그때부터 파일을 모으거나 지난 대화를 찾지 않습니다.</p>
            <p>② 학생 명단·성적표·상담 기록·생활기록부처럼 학생 정보로 보이는 파일과 주민등록번호 또는 여러 개의 휴대폰 번호가 들어 있는 파일은 자동으로 모으지 않습니다. 이런 파일은 이용자가 직접 저장을 선택한 경우에만 자료함에 담깁니다.</p>
            <p>③ 자료함에 모은 파일과 대화 검색용 요약은 AI가 지금 요청에 필요하다고 판단한 경우에만 그 일부를 찾아 입력 텍스트로 AI 제공사(제6조)에 보냅니다. 보내기 전에 연락처·주민등록번호 등은 자동으로 가리며, 지난 대화의 학생 이름 등은 가린 형태로만 참고합니다.</p>
            <p>④ 대화를 삭제하면 그 대화에 올린 첨부 파일도 함께 삭제합니다. 이미 자료함에 담긴 사본은 남으며, 이용자는 AI 맞춤 설정의 참고 자료에서 언제든 확인·삭제할 수 있습니다.</p>
            <p>⑤ 회사는 자료함과 대화 검색용 요약을 광고·마케팅, 제3자 판매 또는 회사·AI 제공사의 모델 학습 목적으로 이용하지 않습니다.</p>
          </PolicySection>
        )}

        <PolicySection title="제4조 (개인정보의 제3자 제공)">
          <p>① 회사는 정보주체의 개인정보를 제2조(개인정보의 처리 목적)에서 명시한 범위 내에서만 처리하며, 정보주체의 동의, 법률의 특별한 규정 등 「개인정보 보호법」 제17조 및 제18조에 해당하는 경우에만 개인정보를 제3자에게 제공합니다.</p>
          
          <div className="policy-card policy-card--success">
            <p>
              <strong>② [중요] AI 서비스 관련:</strong><br/>
              회사는 생성형 AI 서비스 이용과 관련하여, 고객이 입력한 데이터를 AI 벤더의 자체 모델 학습 목적으로 제공하지 않습니다. 회사는 벤더와의 기업용 계약(Enterprise Agreement) 또는 학습 방지 설정(Opt-out)을 통해 고객의 데이터가 벤더의 독자적 이익을 위해 사용되지 않도록 기술적·법적 조치를 취하고 있습니다.
            </p>
          </div>

          <p>③ 다음 각 호의 경우에는 예외적으로 이용자의 동의 없이 제3자에게 제공할 수 있습니다:</p>
          <ul>
            <li>법률의 특별한 규정이 있거나 법령상 의무를 준수하기 위하여 불가피한 경우</li>
            <li>수사, 조사 목적으로 관련 법령에서 정한 절차와 방법에 따라 수사기관 및 감독당국이 개인정보 제공을 요구하는 경우</li>
          </ul>
        </PolicySection>

        <PolicySection title="제5조 (개인정보처리의 위탁)">
          <p>① 회사는 원활한 개인정보 업무처리를 위하여 다음과 같이 개인정보 처리업무를 위탁하고 있습니다:</p>
          
          <h3>1. 국내 수탁사</h3>
          <div className="policy-table-wrap">
            <table className="policy-table">
              <thead>
                <tr>
                  <th scope="col">수탁업체</th>
                  <th scope="col">위탁 업무 내용</th>
                  <th scope="col">보유 및 이용 기간</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>토스페이먼츠</td>
                  <td>결제 대행 서비스</td>
                  <td>회원 탈퇴 또는 위탁 계약 종료 시까지</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>2. 국외 수탁사 ({newsletterPolicyEnabled ? 'AI 서비스, 클라우드, 이메일 발송' : 'AI 서비스'})</h3>
          <p className="policy-muted">
            회사는 AI 기술 제공 및 안정적인 서비스 운영을 위해 데이터 처리를 국외 업체에 위탁하고 있습니다.{newsletterPolicyEnabled && ' 회원 안내·결제 서류·문의 답변 메일과 교육 뉴스레터 발송은 Plus Five Five, Inc.(서비스명 Resend)에 위탁합니다.'} (상세 내용은 제6조 참조)
          </p>

          <p>② 회사는 위탁계약 체결 시 「개인정보 보호법」 제26조에 따라 위탁업무 수행목적 외 개인정보 처리금지, 기술적·관리적 보호조치, 재위탁 제한, 수탁자에 대한 관리·감독, 손해배상 등 책임에 관한 사항을 계약서 등 문서에 명시하고, 수탁자가 개인정보를 안전하게 처리하는지를 감독하고 있습니다.</p>
        </PolicySection>

        <PolicySection title="제6조 (개인정보의 국외 이전)">
          <p>회사는 「개인정보 보호법」 제28조의8 제1항 제3호 가목에 따라, 정보주체와의 계약 체결 및 이행을 위하여 개인정보의 처리 위탁이 필요한 경우로서, 개인정보가 국외로 이전되는 사항을 다음과 같이 공개합니다.</p>
          
          <div className="policy-table-wrap">
            <table className="policy-table">
              <thead>
                <tr>
                  <th scope="col">구분</th>
                  <th scope="col">OpenAI</th>
                  <th scope="col">Anthropic</th>
                  <th scope="col">Google</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>이전받는 자</strong></td>
                  <td>OpenAI, L.L.C.<br/><span className="policy-muted">privacy@openai.com</span></td>
                  <td>Anthropic, PBC<br/><span className="policy-muted">privacy@anthropic.com</span></td>
                  <td>Google LLC<br/><span className="policy-muted">국내대리인: (주)디에이전트</span></td>
                </tr>
                <tr>
                  <td><strong>이전 국가</strong></td>
                  <td>미국</td>
                  <td>미국</td>
                  <td>미국</td>
                </tr>
                <tr>
                  <td><strong>이전 항목</strong></td>
                  <td>입력 텍스트(프롬프트), 이용 로그</td>
                  <td>입력 텍스트(프롬프트), 이용 로그</td>
                  <td>
                    {studentDataPolicyEnabled
                      ? '입력 텍스트(프롬프트), 첨부 파일 내용, 이용 로그, 회원 정보와 서비스 이용 중 입력·생성된 자료(학급 명단, 서술형 채점 기록, 과제 제출물 포함)'
                      : '입력 텍스트(프롬프트), 첨부 파일 내용, 이용 로그'}
                  </td>
                </tr>
                <tr>
                  <td><strong>이전 목적</strong></td>
                  <td>AI 모델 기반 응답 생성</td>
                  <td>AI 언어 모델 기반 텍스트 분석</td>
                  <td>{studentDataPolicyEnabled ? 'AI 플랫폼 운영, 클라우드 서버 운영과 데이터 저장(Google Cloud·Firebase)' : 'AI 플랫폼 운영'}</td>
                </tr>
                <tr>
                  <td><strong>보유 기간</strong></td>
                  <td>목적 달성 시까지 (최대 30일)</td>
                  <td>목적 달성 시까지 (최대 30일)</td>
                  <td>위탁 계약 종료 시까지</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="policy-muted">
            ※ 이전 일시 및 방법: AI 기능 이용 시 정보통신망을 통해 수시 전송 (암호화 전송)
          </p>
          {studentDataPolicyEnabled && (
            <p className="policy-muted">
              ※ 서비스 서버와 파일 저장소는 미국(아이오와)에, 데이터베이스는 대한민국(서울)에 있습니다. 서비스 이용 시 정보통신망을 통해 수시로 암호화 전송되며, 회원 탈퇴 또는 위탁 계약 종료 시까지 보관합니다.
            </p>
          )}

          {newsletterPolicyEnabled && (
            <>
              <h3>이메일 발송</h3>
              <div className="policy-table-wrap">
                <table className="policy-table policy-table--key-value">
                  <tbody>
                    <tr>
                      <th scope="row">이전받는 자</th>
                      <td>Plus Five Five, Inc. (서비스명 Resend)<br/><span className="policy-muted">privacy@resend.com</span></td>
                    </tr>
                    <tr>
                      <th scope="row">이전 국가</th>
                      <td>미국</td>
                    </tr>
                    <tr>
                      <th scope="row">이전 항목</th>
                      <td>받는 사람 이메일 주소, 메일 본문과 첨부 파일(결제 서류 등), 발송·수신 결과 기록</td>
                    </tr>
                    <tr>
                      <th scope="row">이전 목적</th>
                      <td>회원 안내·결제 서류·문의 답변 메일과 교육 뉴스레터(이용자가 구독한 경우) 발송</td>
                    </tr>
                    <tr>
                      <th scope="row">이전 시기 및 방법</th>
                      <td>메일을 보낼 때마다 정보통신망을 통해 암호화 전송</td>
                    </tr>
                    <tr>
                      <th scope="row">보유 기간</th>
                      <td>발송 기록은 30일 보관 후 삭제. 영구 반송된 주소는 재발송을 막기 위해 위탁 계약 종료 시까지 보관될 수 있음</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="policy-muted">
                ※ 교육 뉴스레터의 기사 수집·요약에는 AI를 사용하지만, 구독자의 이메일 주소 등 개인정보는 AI 제공사에 보내지 않습니다.
              </p>
            </>
          )}
        </PolicySection>

        <PolicySection title="제7조 (개인정보의 국외 이전에 대한 거부권)">
          <p>① 정보주체는 회사의 개인정보 국외 이전에 대하여 거부할 권리가 있습니다.</p>
          
          <div className="policy-card policy-card--warning">
            <p>
              <strong>② 행사 방법:</strong><br/>
              국외 이전을 거부하고자 하는 고객님께서는 회사의 개인정보 보호책임자(angel@plactical.com) 또는 고객센터로 서면, 전화, 전자우편 등을 통해 거부 의사를 표시하실 수 있습니다.<br/><br/>
              
              <strong>③ 거부의 효과:</strong><br/>
              • <strong>서비스 이용 제한:</strong> 국외 이전되는 개인정보는 AI 기반 서비스를 제공하기 위해 필수적인 데이터입니다. 따라서 이전을 거부하실 경우, AI 생기부 생성, AI 상담 등 AI 기술이 적용된 서비스의 이용이 제한될 수 있습니다.<br/>
              • <strong>계약 해지:</strong> 국외 이전을 거부함으로써 회사가 본질적인 서비스를 제공할 수 없는 경우, 부득이하게 서비스 이용 계약이 해지될 수 있습니다.
              {newsletterPolicyEnabled && (
                <>
                  <br/>• <strong>이메일 발송:</strong> 이메일 발송 위탁을 거부하시면 회원 안내 메일과 결제 서류를 메일로 받을 수 없습니다. 교육 뉴스레터는 메일 맨 아래 '수신거부'를 누르면 바로 발송이 멈추고 저장된 구독 정보도 지워집니다.
                </>
              )}
            </p>
          </div>
        </PolicySection>

        <PolicySection title="제8조 (만 14세 미만 아동의 개인정보 보호)">
          <div className="policy-card policy-card--danger">
            <p>
              <strong>① 회사는 만 14세 미만 아동의 개인정보 보호를 위해 다음과 같은 조치를 취합니다:</strong><br/><br/>
              • 회사의 서비스는 원칙적으로 <strong>교사(성인)</strong>를 대상으로 제공됩니다.<br/>
              • 만 14세 미만 아동이 직접 서비스에 가입하는 것을 허용하지 않습니다.<br/>
              • 만 14세 미만 아동의 개인정보를 수집해야 하는 경우, 반드시 <strong>법정대리인의 동의</strong>를 받습니다.<br/><br/>
              
              <strong>② 법정대리인의 권리:</strong><br/>
              • 법정대리인은 아동의 개인정보에 대한 열람, 정정, 삭제, 처리정지를 요청할 수 있습니다.<br/>
              • 법정대리인의 동의 없이 수집된 만 14세 미만 아동의 개인정보는 지체 없이 파기합니다.<br/><br/>
              
              <strong>③ 동의 방법:</strong><br/>
              법정대리인의 동의가 필요한 경우, 이메일 또는 서면을 통해 동의를 받으며, 법정대리인 본인 확인 후 동의 절차를 진행합니다.
            </p>
          </div>
          {studentDataPolicyEnabled && (
            <p>④ 학급 명단, 서술형 채점, 과제 제출방의 학생 정보는 회사가 학생에게서 직접 동의를 받아 수집하는 정보가 아니라, 이용자(교사·학교)가 학교 업무를 위해 처리하는 정보를 회사가 이용자를 위하여 처리하는 것입니다. 과제 제출방에서 학생은 로그인 없이 이름과 과제만 제출하며, 회사는 학생에게 연락처나 법정대리인 정보를 요구하지 않습니다.</p>
          )}
        </PolicySection>

        <PolicySection title="제9조 (정보주체의 권리·의무 및 행사방법)">
          <p>① 정보주체는 회사에 대해 언제든지 다음 각 호의 개인정보 보호 관련 권리를 행사할 수 있습니다:</p>
          <ol>
            <li>개인정보 열람 요구</li>
            <li>오류 등이 있을 경우 정정 요구</li>
            <li>삭제 요구</li>
            <li>처리정지 요구</li>
          </ol>

          <p>② 제1항에 따른 권리 행사는 회사에 대해 「개인정보 보호법」 시행령 제41조 제1항에 따라 서면, 전자우편, 모사전송(FAX) 등을 통하여 하실 수 있으며, 회사는 이에 대해 지체 없이 조치하겠습니다.</p>
          
          <p>③ 이용자가 개인정보의 오류 등에 대한 정정 또는 삭제를 요구한 경우에는 회사는 정정 또는 삭제를 완료할 때까지 당해 개인정보를 이용하거나 제3자에게 제공하지 않습니다.</p>

          <p>④ 제1항에 따른 권리 행사는 이용자의 법정대리인이나 위임을 받은 자 등 대리인을 통하여 하실 수 있습니다. 이 경우 「개인정보 보호법」 시행규칙 별지 제11호 서식에 따른 위임장을 제출하여야 합니다.</p>

          <div className="policy-card policy-card--info">
            <p>
              <strong>⑤ 자동화된 결정에 대한 권리:</strong><br/>
              회사가 AI 기술을 이용하여 완전히 자동화된 시스템으로 개인정보를 처리하고, 그 결과가 정보주체의 권리 또는 의무에 중대한 영향을 미치는 경우, 정보주체는 해당 결정에 대해 거부하거나 설명을 요구할 권리가 있습니다.
            </p>
          </div>
        </PolicySection>

        <PolicySection title="제10조 (개인정보 자동 수집 장치의 설치·운용 및 거부)">
          <p>① 회사는 이용자에게 개인화된 서비스를 제공하기 위해 이용자의 정보를 저장하고 수시로 불러오는 '쿠키(cookie)'를 사용합니다.</p>
          
          <p>② 쿠키란 웹사이트를 운영하는데 이용되는 서버가 이용자의 브라우저에 보내는 아주 작은 텍스트 파일로서 이용자의 컴퓨터 하드디스크에 저장됩니다.</p>

          <h3>③ 쿠키 사용 목적</h3>
          <ul>
            <li>사이트 방문 및 이용 형태 파악하여 이용자에게 최적화된 이용 환경 제공</li>
            <li>로그인 상태 유지</li>
            <li>서비스 개선을 위한 통계 분석</li>
          </ul>

          <h3>④ 쿠키 설정 거부 방법</h3>
          <p>이용자는 쿠키 설치에 대한 선택권을 가지고 있습니다. 웹 브라우저 옵션에서 모든 쿠키를 허용하거나, 쿠키가 저장될 때마다 확인을 거치거나, 모든 쿠키의 저장을 거부할 수 있습니다.</p>
          
          <div className="policy-card">
            <p>
              <strong>설정 방법 예시:</strong><br/>
              • Chrome: 설정 → 개인정보 및 보안 → 쿠키 및 기타 사이트 데이터<br/>
              • Edge: 설정 → 쿠키 및 사이트 권한 → 쿠키 및 사이트 데이터 관리<br/>
              • Safari: 환경설정 → 개인정보 보호 → 쿠키 및 웹 사이트 데이터<br/>
              <br/>
              <strong>※ 주의:</strong> 쿠키 저장을 거부하실 경우 로그인이 필요한 일부 서비스 이용에 어려움이 있을 수 있습니다.
            </p>
          </div>
        </PolicySection>

        <PolicySection title="제11조 (개인정보의 안전성 확보조치)">
          <p>회사는 개인정보의 안전성 확보를 위해 다음과 같은 조치를 취하고 있습니다:</p>
          
          <h3>1. 관리적 조치</h3>
          <ul>
            <li>내부관리계획 수립·시행</li>
            <li>정기적 직원 교육</li>
            <li>AI 벤더에 대한 정기적 점검 및 관리 감독</li>
            <li>개인정보 처리 직원의 최소화</li>
          </ul>

          <h3>2. 기술적 조치</h3>
          <ul>
            <li>개인정보처리시스템 등의 접근권한 관리</li>
            <li>접근통제시스템 설치</li>
            <li>고유식별정보 등의 암호화</li>
            <li>보안프로그램 설치 및 주기적 갱신</li>
            <li>국외 전송 구간의 암호화 (SSL/TLS)</li>
          </ul>

          <h3>3. 물리적 조치</h3>
          <ul>
            <li>전산실, 자료보관실 등의 접근통제</li>
          </ul>
        </PolicySection>

        <PolicySection title="제12조 (개인정보보호책임자)">
          <p>① 회사는 개인정보를 안전하게 이용하여 최상의 서비스를 제공하기 위하여 최선을 다하고 있습니다.</p>
          
          <div className="policy-card policy-card--success">
            <p><strong>개인정보보호책임자</strong></p>
            <ul>
              <li>성명: 강연구</li>
              <li>직책: 대표이사</li>
              <li>이메일: angel@plactical.com</li>
              <li>전화: 02-949-9158</li>
            </ul>
          </div>

          <p>② 이용자는 회사의 서비스를 이용하시며 발생하는 모든 개인정보보호 관련 민원을 개인정보 보호책임자에게 신고하실 수 있습니다. 회사는 이용자들의 신고사항에 대해 신속하게 충분한 답변을 드릴 것입니다.</p>
        </PolicySection>

        <PolicySection title="제13조 (권익침해 구제방법)">
          <p>정보주체는 개인정보침해로 인한 구제를 받기 위하여 아래 기관에 분쟁해결이나 상담 등을 신청할 수 있습니다.</p>
          
          <div className="policy-card">
            <ul>
              <li>
                <strong>개인정보침해 신고센터</strong><br/>
                (국번없이) 118 | privacy.kisa.or.kr
              </li>
              <li>
                <strong>개인정보 분쟁조정위원회</strong><br/>
                1833-6972 | www.kopico.go.kr
              </li>
              <li>
                <strong>대검찰청</strong><br/>
                (국번없이) 1301 | www.spo.go.kr
              </li>
              <li>
                <strong>경찰청</strong><br/>
                (국번없이) 182 | ecrm.cyber.go.kr
              </li>
            </ul>
          </div>
        </PolicySection>

        <PolicySection title="제14조 (개인정보처리방침의 변경)">
          <p>
            ① 이 개인정보처리방침은 2026년 1월 28일부터 적용되며, 학교생활기록 처리 관련 개정사항은 2026년 7월 29일부터 적용됩니다.
            {personalizationPolicyEnabled && ' AI 맞춤 설정 관련 개정사항은 2026년 8월 7일부터 적용됩니다.'}
            {partnerPolicyEnabled && ' 자율연수 강사 계약·정산 관련 개정사항은 2026년 8월 31일부터 적용됩니다.'}
            {studentDataPolicyEnabled && ' 학급 명단·서술형 채점·과제 제출방 학생 정보 관련 개정사항은 2026년 10월 18일부터 적용됩니다.'}
            {studentNotesPolicyEnabled && ' 학생 메모·임원·진로희망 관련 개정사항은 2026년 10월 19일부터 적용됩니다.'}
            {newsletterPolicyEnabled && ' 교육 뉴스레터 구독·이메일 발송 위탁 관련 개정사항은 2026년 10월 21일부터 적용됩니다.'}
            {aiReferencePolicyEnabled && ` 내 자료함 자동 모으기·지난 대화 찾아보기·생기부 근거 보기 관련 개정사항은 ${POLICY_V10_REVISED_LABEL}부터 적용됩니다.`}
          </p>
          
          <div className="policy-card policy-card--info">
            <p>
              ② 주요 변경사항 (2026년 1월 28일):<br/>
              • <strong>만 14세 미만 아동 보호 조항 신설</strong> - 법정대리인 동의 절차 명시<br/>
              • <strong>개인정보 수집 항목·방법 상세화</strong> - 수집 항목별 세분화<br/>
              • <strong>파기 절차 및 방법 상세화</strong> - 파기 절차, 방법 구체적 기술<br/>
              • <strong>쿠키(자동 수집 장치) 조항 신설</strong> - 쿠키 사용 목적 및 거부 방법 안내<br/>
              • <strong>보유기간 법적 근거 명시</strong> - 관련 법령 근거 표 추가<br/>
              • <strong>권익침해 구제방법 상세화</strong> - 기관 연락처 명시
            </p>
          </div>

          <div className="policy-card policy-card--info">
            <p>
              ③ 주요 변경사항 (2026년 7월 29일):<br/>
              • <strong>학교생활기록 목적 제한</strong> - 교사의 학생 지도·기록 작성 및 검수 범위에서만 처리<br/>
              • <strong>검사 초안 개인정보 보호</strong> - 연락처 등 개인정보를 마스킹한 형태로 기록<br/>
              • <strong>원본 파일 미보관</strong> - 검사에 업로드한 파일 자체는 별도 파일로 저장하지 않음<br/>
              • <strong>목적 외 이용 금지</strong> - 입시 상담, 판매, 광고, 유료 자료 및 AI 모델 학습에 이용하지 않음<br/>
              • <strong>회원 탈퇴 삭제 범위 보완</strong> - 생기부 생성·검사 기록과 검사 사용 기록 함께 삭제<br/>
              • 자세한 내용은 <a href="/help/notice/saenggibu-commercial-use-law-2026">법 개정 대응 안내</a>에서 확인할 수 있습니다.
            </p>
          </div>

          {personalizationPolicyEnabled && (
            <div className="policy-card policy-card--info">
              <p>
                ④ 주요 변경사항 (2026년 8월 7일):<br/>
                • <strong>AI 맞춤 설정 신설</strong> - 선택 입력한 교사 업무 정보와 답변 선호를 관련 요청에 반영<br/>
                • <strong>학교 이름 사용 범위 제한</strong> - 기관명 자리가 필요한 문서에서만 사용<br/>
                • <strong>AI 기억 직접 관리</strong> - 마이페이지에서 확인·수정·삭제 및 사용 중지 가능<br/>
                • <strong>학생 개인정보 기억 금지</strong> - 학생 식별정보와 수정·요약 대상 자료는 자동 기억에서 제외
              </p>
            </div>
          )}

          {partnerPolicyEnabled && (
            <div className="policy-card policy-card--info">
              <p>
                ⑤ 주요 변경사항 (2026년 8월 31일):<br/>
                • <strong>강사 계약자료 명시</strong> - 최종 계약서와 겸직허가·불필요 서면확인의 처리항목 공개<br/>
                • <strong>정산·세무 목적 명확화</strong> - 판매·환불·인세·원천징수와 지급명세서 처리 목적 공개<br/>
                • <strong>민감정보 수집경로 제한</strong> - 주민등록번호·계좌정보를 일반 이메일과 공개 화면에서 수집하지 않음<br/>
                • <strong>보유기간 공개</strong> - 계약 종료 또는 최종 정산 후 5년 보관
              </p>
            </div>
          )}
          
          {studentDataPolicyEnabled && (
            <div className="policy-card policy-card--info">
              <p>
                ⑥ 주요 변경사항 (2026년 10월 18일):<br/>
                • <strong>학생 정보 처리항목 공개</strong> - 학급 명단(학생 이름·번호·이름 별칭), 서술형 채점 기록, 과제 제출방 제출물<br/>
                • <strong>처리 목적 추가</strong> - 학생별 기록 모아보기와 이용자 요청 시 이를 근거로 한 생활기록부 초안 작성<br/>
                • <strong>수집 방법 추가</strong> - 이름을 고칠 때 저장되는 이름 별칭, 학생이 제출 링크에서 직접 제출<br/>
                • <strong>보유기간 공개</strong> - 학급 명단·채점 기록·제출물은 이용자 삭제 또는 탈퇴 시까지, 처리용 임시 파일은 1~7일<br/>
                • <strong>학생 정보 특칙 신설</strong> - 이용자 요청 범위에서만 처리, 명단은 AI 제공사에 보내지 않음, 학생 화면에 명단 미노출, 목적 외 이용 금지<br/>
                • <strong>클라우드 위치 명시</strong> - 서버·파일 저장소(미국)와 데이터베이스(대한민국 서울) 위치와 Google Cloud 처리 내용 공개
              </p>
            </div>
          )}

          {studentNotesPolicyEnabled && (
            <div className="policy-card policy-card--info">
              <p>
                ⑦ 주요 변경사항 (2026년 10월 19일):<br/>
                • <strong>학생 메모 처리항목 추가</strong> - 이용자가 학생별로 남긴 관찰 메모와 작성일·영역, 임원·진로희망<br/>
                • <strong>보유기간 공개</strong> - 이용자 삭제 또는 탈퇴 시까지, 명단을 지우면 남은 명단에 없는 학생의 메모도 함께 삭제<br/>
                • <strong>AI 전송 범위 명시</strong> - 이용자가 생활기록부 초안 작성에 넣은 경우에만 입력 텍스트로 전송
              </p>
            </div>
          )}

          {newsletterPolicyEnabled && (
            <div className="policy-card policy-card--info">
              <p>
                ⑧ 주요 변경사항 (2026년 10월 21일):<br/>
                • <strong>교육 뉴스레터 처리항목 추가</strong> - 이메일 주소, 동의 기록(동의 일시·문구·IP 주소·브라우저 정보), 로그인 상태에서 신청 시 회원 식별값<br/>
                • <strong>처리 목적 추가</strong> - 아침 교육 뉴스와 왓퀴즈 새 기능·이벤트 소식 발송, 수신동의 처리 결과와 2년마다 수신동의 확인 안내<br/>
                • <strong>보유기간 공개</strong> - 수신거부하면 즉시 파기, 확정하지 않은 신청은 7일 뒤 파기<br/>
                • <strong>이메일 발송 국외 위탁 공개</strong> - Plus Five Five, Inc.(Resend, 미국)의 이전 항목·목적·시기·보유기간
              </p>
            </div>
          )}

          {aiReferencePolicyEnabled && (
            <div className="policy-card policy-card--info">
              <p>
                ⑨ 주요 변경사항 ({POLICY_V10_REVISED_LABEL}):<br/>
                • <strong>내 자료함 자동 모으기 신설</strong> - 허용한 이용자의 채팅 문서 파일 사본을 자료함에 모음(학생 정보로 보이는 파일 제외)<br/>
                • <strong>지난 대화 찾아보기 신설</strong> - 개인정보를 가린 대화 검색용 요약을 만들어 필요한 경우에만 참고<br/>
                • <strong>채팅 첨부 보유기간 명시</strong> - 그 대화를 삭제하거나 회원 탈퇴 시까지, 대화를 지우면 첨부도 함께 삭제<br/>
                • <strong>동의 기록</strong> - 기능별 허용 여부·시각·처리방침 버전을 기록하고 AI 맞춤 설정에서 각각 끌 수 있음<br/>
                • <strong>생기부 근거 보기 자료 보관</strong> - 생성 때 입력한 내용과 첨부 파일에서 읽은 글(개인정보를 가린 형태)을 생기부 기록을 지우거나 탈퇴할 때까지 보관, 원본 파일은 보관하지 않음
              </p>
            </div>
          )}

          <p>{aiReferencePolicyEnabled ? '⑩' : newsletterPolicyEnabled ? '⑨' : studentNotesPolicyEnabled ? '⑧' : studentDataPolicyEnabled ? '⑦' : partnerPolicyEnabled ? '⑥' : personalizationPolicyEnabled ? '⑤' : '④'} 이전의 개인정보처리방침은 아래에서 확인하실 수 있습니다.</p>
          <ul>
            {aiReferencePolicyEnabled && (
              <li><a href="/privacy-policy?preview=v9">2026. 10. 21 ~ 2026. 11. 1 적용 지침</a></li>
            )}
            {newsletterPolicyEnabled && (
              <li><a href="/privacy-policy?preview=v8">2026. 10. 19 ~ 2026. 10. 20 적용 지침</a></li>
            )}
            {studentNotesPolicyEnabled && (
              <li><a href="/privacy-policy?preview=v7">2026. 10. 18 ~ 2026. 10. 18 적용 지침</a></li>
            )}
            {studentDataPolicyEnabled && (
              <li><a href="/privacy-policy?preview=v6">2026. 8. 31 ~ 2026. 10. 17 적용 지침</a></li>
            )}
            {personalizationPolicyEnabled && (
              <li><a href="/privacy-policy?preview=v3">2026. 1. 28 ~ 2026. 8. 12 적용 지침</a></li>
            )}
            <li><a href="/privacy-policy?preview=v2">2025. 12. 18 ~ 2026. 1. 27 적용 지침</a></li>
            <li><a href="/help/notice/privacy-policy-previous-2025-01">2025. 1. 15 ~ 2025. 12. 17 적용 지침</a></li>
          </ul>
        </PolicySection>

        <PolicyFooterV3 />
      </main>
    </div>
  );
};

// ============================================================
// V2 개인정보처리방침 (2025-12-18 ~ 2026-01-27 적용)
// AI 서비스 국외 이전 내용 포함
// ============================================================
const PrivacyPolicyV2 = ({ isPreview = false }) => {
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      padding: 'clamp(1rem, 4vw, 2rem)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: 'clamp(1.5rem, 5vw, 3rem)',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        lineHeight: 1.7
      }}>
        <h1 style={{
          fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
          fontWeight: '700',
          color: '#1a202c',
          marginBottom: '2rem',
          textAlign: 'center'
        }}>
          개인정보처리방침
        </h1>

        {/* 미리보기 모드 배너 (V2) */}
        {isPreview && (
          <div style={{
            backgroundColor: '#f0f9ff',
            padding: '1rem 1.5rem',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            border: '2px solid #7dd3fc'
          }}>
            <p style={{
              fontSize: '14px',
              color: '#0369a1',
              margin: 0,
              textAlign: 'center',
              fontWeight: '600'
            }}>
              이전 버전 보기 - 2025년 12월 18일 ~ 2026년 1월 27일 적용된 방침입니다
            </p>
          </div>
        )}

        {/* 변경 예정 안내 배너 */}
        {!isPreview && (
          <div style={{
            backgroundColor: '#fef3c7',
            padding: '1rem 1.5rem',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            border: '1px solid #fde68a'
          }}>
            <p style={{
              fontSize: '14px',
              color: '#92400e',
              margin: 0,
              lineHeight: 1.6
            }}>
              <strong>개인정보처리방침 변경 예정 안내</strong><br/>
              만 14세 미만 아동 보호, 쿠키 관련 조항 등이 강화된 개인정보처리방침이 2026년 1월 28일부터 적용됩니다. 
              <a href="/privacy-policy?preview=v3" style={{ color: '#b45309', marginLeft: '4px' }}>미리보기</a> | 
              <a href="/help?tab=notice" style={{ color: '#b45309', marginLeft: '4px' }}>공지사항</a>
            </p>
          </div>
        )}

        <div style={{
          backgroundColor: '#f0f4ff',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          border: '1px solid #c7d2fe'
        }}>
          <p style={{
            fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
            color: '#3730a3',
            margin: 0,
            textAlign: 'center',
            fontWeight: '600'
          }}>
            시행일: 2025년 12월 18일 | 최종 개정일: 2025년 12월 18일
          </p>
        </div>

        <p style={{ marginBottom: '2rem', color: '#4a5568' }}>
          플랙티컬 주식회사(이하 "회사")는 「개인정보 보호법」 제30조에 따라 정보주체의 개인정보를 보호하고 이와 관련한 고충을 신속하고 원활하게 처리할 수 있도록 하기 위하여 다음과 같이 개인정보처리방침을 수립·공개합니다.
        </p>

        <Section title="제1조 (개인정보의 처리 목적)">
          <p>회사는 다음의 목적을 위하여 개인정보를 처리합니다. 처리하고 있는 개인정보는 다음의 목적 이외의 용도로는 이용되지 않으며, 이용 목적이 변경되는 경우에는 「개인정보 보호법」 제18조에 따라 별도의 동의를 받는 등 필요한 조치를 이행할 예정입니다.</p>
          
          <ol style={{ paddingLeft: '1.5rem', marginTop: '1rem' }}>
            <li style={{ marginBottom: '0.5rem' }}><strong>서비스 가입 및 관리:</strong> 회원 가입 의사 확인, 회원제 서비스 제공에 따른 본인 식별·인증, 회원자격 유지·관리, 서비스 부정이용 방지 등</li>
            <li style={{ marginBottom: '0.5rem' }}><strong>서비스 제공:</strong> 콘텐츠 제공, AI 기반 기능(생기부 작성, AI 상담, 데이터 분석 등) 제공, 요금결제·정산 등</li>
            <li style={{ marginBottom: '0.5rem' }}><strong>고충처리:</strong> 민원인의 신원 확인, 민원사항 확인, 사실조사를 위한 연락·통지, 처리결과 통보 등</li>
          </ol>
        </Section>

        <Section title="제2조 (개인정보의 처리 및 보유기간)">
          <p>① 회사는 법령에 따른 개인정보 보유·이용기간 또는 정보주체로부터 개인정보를 수집 시에 동의받은 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.</p>
          
          <div style={{ marginTop: '1rem' }}>
            <h4 style={{ color: '#2d3748', marginBottom: '0.5rem' }}>② 각각의 개인정보 처리 및 보유 기간은 다음과 같습니다:</h4>
            <ul style={{ paddingLeft: '1.5rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>서비스 이용 정보: 회원 탈퇴 시까지</li>
              <li style={{ marginBottom: '0.5rem' }}>결제정보: 전자상거래법에 따라 5년</li>
              <li style={{ marginBottom: '0.5rem' }}>고객 문의 기록: 3년</li>
            </ul>
          </div>
        </Section>

        <Section title="제3조 (개인정보의 제3자 제공)">
          <p>① 회사는 정보주체의 개인정보를 제1조(개인정보의 처리 목적)에서 명시한 범위 내에서만 처리하며, 정보주체의 동의, 법률의 특별한 규정 등 「개인정보 보호법」 제17조 및 제18조에 해당하는 경우에만 개인정보를 제3자에게 제공합니다.</p>
          
          <div style={{
            backgroundColor: '#f0fdf4',
            padding: '1.5rem',
            borderRadius: '12px',
            marginTop: '1rem',
            border: '1px solid #bbf7d0'
          }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#166534', lineHeight: 1.6 }}>
              <strong>② [중요] AI 서비스 관련:</strong><br/>
              회사는 생성형 AI 서비스 이용과 관련하여, 고객이 입력한 데이터를 AI 벤더의 자체 모델 학습 목적으로 제공하지 않습니다. 회사는 벤더와의 기업용 계약(Enterprise Agreement) 또는 학습 방지 설정(Opt-out)을 통해 고객의 데이터가 벤더의 독자적 이익을 위해 사용되지 않도록 기술적·법적 조치를 취하고 있습니다.
            </p>
          </div>
        </Section>

        <Section title="제4조 (개인정보처리의 위탁)">
          <p>① 회사는 원활한 개인정보 업무처리를 위하여 다음과 같이 개인정보 처리업무를 위탁하고 있습니다:</p>
          
          <h4 style={{ color: '#2d3748', marginTop: '1.5rem', marginBottom: '1rem' }}>1. 국내 수탁사</h4>
          <div style={{
            backgroundColor: '#f7fafc',
            padding: '1rem',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            overflowX: 'auto'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '8px', textAlign: 'left', fontWeight: '600' }}>수탁업체</th>
                  <th style={{ padding: '8px', textAlign: 'left', fontWeight: '600' }}>위탁 업무 내용</th>
                  <th style={{ padding: '8px', textAlign: 'left', fontWeight: '600' }}>보유 및 이용 기간</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '8px' }}>토스페이먼츠</td>
                  <td style={{ padding: '8px' }}>결제 대행 서비스</td>
                  <td style={{ padding: '8px' }}>회원 탈퇴 또는 위탁 계약 종료 시까지</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h4 style={{ color: '#2d3748', marginTop: '1.5rem', marginBottom: '1rem' }}>2. 국외 수탁사 (AI 서비스)</h4>
          <p style={{ fontSize: '14px', color: '#4a5568', marginBottom: '1rem' }}>
            회사는 AI 기술 제공 및 안정적인 서비스 운영을 위해 데이터 처리를 국외 업체에 위탁하고 있습니다. 이는 서비스 제공 계약의 이행을 위해 필수적인 업무 위탁에 해당합니다. (상세 내용은 제5조 참조)
          </p>

          <p style={{ marginTop: '1rem' }}>② 회사는 위탁계약 체결 시 「개인정보 보호법」 제26조에 따라 위탁업무 수행목적 외 개인정보 처리금지, 기술적·관리적 보호조치, 재위탁 제한, 수탁자에 대한 관리·감독, 손해배상 등 책임에 관한 사항을 계약서 등 문서에 명시하고, 수탁자가 개인정보를 안전하게 처리하는지를 감독하고 있습니다.</p>
        </Section>

        <Section title="제5조 (개인정보의 국외 이전)">
          <p>회사는 「개인정보 보호법」 제28조의8 제1항 제3호 가목에 따라, 정보주체와의 계약 체결 및 이행을 위하여 개인정보의 처리 위탁이 필요한 경우로서, 개인정보가 국외로 이전되는 사항을 다음과 같이 공개합니다.</p>
          
          <div style={{
            backgroundColor: '#f7fafc',
            padding: '1rem',
            borderRadius: '8px',
            marginTop: '1rem',
            border: '1px solid #e2e8f0',
            overflowX: 'auto'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0', backgroundColor: '#f1f5f9' }}>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600' }}>구분</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600' }}>OpenAI</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600' }}>Anthropic</th>
                  <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600' }}>Google</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '10px', fontWeight: '600' }}>이전받는 자</td>
                  <td style={{ padding: '10px' }}>OpenAI, L.L.C.<br/><span style={{ fontSize: '12px', color: '#6b7280' }}>privacy@openai.com</span></td>
                  <td style={{ padding: '10px' }}>Anthropic, PBC<br/><span style={{ fontSize: '12px', color: '#6b7280' }}>privacy@anthropic.com</span></td>
                  <td style={{ padding: '10px' }}>Google LLC<br/><span style={{ fontSize: '12px', color: '#6b7280' }}>국내대리인: (주)디에이전트</span></td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '10px', fontWeight: '600' }}>이전 국가</td>
                  <td style={{ padding: '10px' }}>미국</td>
                  <td style={{ padding: '10px' }}>미국</td>
                  <td style={{ padding: '10px' }}>미국</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '10px', fontWeight: '600' }}>이전 항목</td>
                  <td style={{ padding: '10px' }}>입력 텍스트(프롬프트), 이용 로그</td>
                  <td style={{ padding: '10px' }}>입력 텍스트(프롬프트), 이용 로그</td>
                  <td style={{ padding: '10px' }}>서비스 이용 기록</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '10px', fontWeight: '600' }}>이전 목적</td>
                  <td style={{ padding: '10px' }}>AI 모델 기반 응답 생성</td>
                  <td style={{ padding: '10px' }}>AI 언어 모델 기반 텍스트 분석</td>
                  <td style={{ padding: '10px' }}>AI 플랫폼 운영</td>
                </tr>
                <tr>
                  <td style={{ padding: '10px', fontWeight: '600' }}>보유 기간</td>
                  <td style={{ padding: '10px' }}>목적 달성 시까지 (최대 30일)</td>
                  <td style={{ padding: '10px' }}>목적 달성 시까지 (최대 30일)</td>
                  <td style={{ padding: '10px' }}>위탁 계약 종료 시까지</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p style={{ marginTop: '1rem', fontSize: '14px', color: '#4a5568' }}>
            ※ 이전 일시 및 방법: AI 기능 이용 시 정보통신망을 통해 수시 전송 (암호화 전송)
          </p>
        </Section>

        <Section title="제6조 (개인정보의 국외 이전에 대한 거부권)">
          <p>① 정보주체는 회사의 개인정보 국외 이전에 대하여 거부할 권리가 있습니다.</p>
          
          <div style={{
            backgroundColor: '#fef3c7',
            padding: '1.5rem',
            borderRadius: '12px',
            marginTop: '1rem',
            border: '1px solid #fde68a'
          }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#92400e', lineHeight: 1.6 }}>
              <strong>② 행사 방법:</strong><br/>
              국외 이전을 거부하고자 하는 고객님께서는 회사의 개인정보 보호책임자(angel@plactical.com) 또는 고객센터로 서면, 전화, 전자우편 등을 통해 거부 의사를 표시하실 수 있습니다.<br/><br/>
              
              <strong>③ 거부의 효과:</strong><br/>
              • <strong>서비스 이용 제한:</strong> 국외 이전되는 개인정보는 AI 기반 서비스를 제공하기 위해 필수적인 데이터입니다. 따라서 이전을 거부하실 경우, AI 생기부 생성, AI 상담 등 AI 기술이 적용된 서비스의 이용이 제한될 수 있습니다.<br/>
              • <strong>계약 해지:</strong> 국외 이전을 거부함으로써 회사가 본질적인 서비스를 제공할 수 없는 경우, 부득이하게 서비스 이용 계약이 해지될 수 있습니다.
            </p>
          </div>
        </Section>

        <Section title="제7조 (정보주체의 권리·의무 및 행사방법)">
          <p>① 정보주체는 회사에 대해 언제든지 개인정보 열람·정정·삭제·처리정지 요구 등의 권리를 행사할 수 있습니다.</p>
          <p>② 제1항에 따른 권리 행사는 회사에 대해 「개인정보 보호법」 시행령 제41조 제1항에 따라 서면, 전자우편, 모사전송(FAX) 등을 통하여 하실 수 있으며, 회사는 이에 대해 지체 없이 조치하겠습니다.</p>
          
          <div style={{
            backgroundColor: '#f0f9ff',
            padding: '1rem',
            borderRadius: '8px',
            marginTop: '1rem',
            border: '1px solid #bae6fd'
          }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#0c4a6e', lineHeight: 1.6 }}>
              <strong>③ 자동화된 결정에 대한 권리:</strong><br/>
              회사가 AI 기술을 이용하여 완전히 자동화된 시스템으로 개인정보를 처리하고, 그 결과가 정보주체의 권리 또는 의무에 중대한 영향을 미치는 경우, 정보주체는 해당 결정에 대해 거부하거나 설명을 요구할 권리가 있습니다.
            </p>
          </div>
        </Section>

        <Section title="제8조 (개인정보의 안전성 확보조치)">
          <p>회사는 개인정보의 안전성 확보를 위해 다음과 같은 조치를 취하고 있습니다:</p>
          <ul style={{ paddingLeft: '1.5rem', marginTop: '1rem' }}>
            <li style={{ marginBottom: '0.5rem' }}><strong>관리적 조치:</strong> 내부관리계획 수립·시행, 정기적 직원 교육, AI 벤더에 대한 정기적 점검 및 관리 감독</li>
            <li style={{ marginBottom: '0.5rem' }}><strong>기술적 조치:</strong> 개인정보처리시스템 등의 접근권한 관리, 접근통제시스템 설치, 고유식별정보 등의 암호화, 보안프로그램 설치, 국외 전송 구간의 암호화(SSL/TLS)</li>
            <li style={{ marginBottom: '0.5rem' }}><strong>물리적 조치:</strong> 전산실, 자료보관실 등의 접근통제</li>
          </ul>
        </Section>

        <Section title="제9조 (개인정보보호책임자)">
          <div style={{
            backgroundColor: '#f0fff4',
            padding: '1.5rem',
            borderRadius: '12px',
            border: '1px solid #9ae6b4'
          }}>
            <p style={{ margin: 0, marginBottom: '1rem', fontWeight: '600', color: '#2f855a' }}>
              개인정보보호책임자
            </p>
            <ul style={{ paddingLeft: '1.5rem', margin: 0 }}>
              <li style={{ marginBottom: '0.5rem' }}>성명: 강연구</li>
              <li style={{ marginBottom: '0.5rem' }}>직책: 대표이사</li>
              <li style={{ marginBottom: '0.5rem' }}>이메일: angel@plactical.com</li>
              <li style={{ marginBottom: '0.5rem' }}>전화: 02-949-9158</li>
            </ul>
          </div>
        </Section>

        <Section title="제10조 (권익침해 구제방법)">
          <p>정보주체는 개인정보침해로 인한 구제를 받기 위하여 개인정보분쟁조정위원회, 한국인터넷진흥원 개인정보침해신고센터 등에 분쟁해결이나 상담 등을 신청할 수 있습니다.</p>
          <ul style={{ paddingLeft: '1.5rem', marginTop: '1rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>개인정보분쟁조정위원회: 1833-6972 (www.kopico.go.kr)</li>
            <li style={{ marginBottom: '0.5rem' }}>개인정보침해신고센터: (국번없이) 118 (privacy.kisa.or.kr)</li>
            <li style={{ marginBottom: '0.5rem' }}>대검찰청: (국번없이) 1301 (www.spo.go.kr)</li>
            <li style={{ marginBottom: '0.5rem' }}>경찰청: (국번없이) 182 (ecrm.cyber.go.kr)</li>
          </ul>
        </Section>

        <Section title="제11조 (개인정보처리방침의 변경)">
          <p>① 이 개인정보처리방침은 2025년 12월 18일부터 적용됩니다.</p>
          
          <div style={{
            backgroundColor: '#f0f4ff',
            padding: '1rem',
            borderRadius: '8px',
            marginTop: '1rem',
            border: '1px solid #c7d2fe'
          }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#3730a3', fontWeight: '600' }}>
              ② 주요 변경사항 (2025년 12월 18일):<br/>
              • <strong>개인정보 국외 이전 조항 신설</strong> - AI 서비스 제공을 위한 국외 이전 사항 공개<br/>
              • <strong>국외 이전 거부권 안내</strong> - 거부 방법 및 효과 명시<br/>
              • <strong>국외 수탁사 정보 공개</strong> - OpenAI, Anthropic, Google 등
            </p>
          </div>
          
          <p style={{ marginTop: '1rem' }}>③ 이전의 개인정보처리방침은 아래에서 확인하실 수 있습니다.</p>
          <ul style={{ paddingLeft: '1.5rem' }}>
            <li><a href="/help/notice/privacy-policy-previous-2025-01" style={{ color: '#1a73e8' }}>2025. 1. 15 ~ 2025. 12. 17 적용 지침</a></li>
          </ul>
        </Section>

        <PolicyFooter />
      </div>
    </div>
  );
};

// ============================================================
// V1 개인정보처리방침 (2025-01-15 ~ 2025-12-17 적용)
// 최초 버전
// ============================================================
const PrivacyPolicyV1 = () => {
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      padding: 'clamp(1rem, 4vw, 2rem)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: 'clamp(1.5rem, 5vw, 3rem)',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        lineHeight: 1.7
      }}>
        <h1 style={{
          fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
          fontWeight: '700',
          color: '#1a202c',
          marginBottom: '2rem',
          textAlign: 'center'
        }}>
          개인정보처리방침
        </h1>

        {/* 이전 버전 안내 배너 */}
        <div style={{
          backgroundColor: '#f0f9ff',
          padding: '1rem 1.5rem',
          borderRadius: '12px',
          marginBottom: '1.5rem',
          border: '2px solid #7dd3fc'
        }}>
          <p style={{
            fontSize: '14px',
            color: '#0369a1',
            margin: 0,
            lineHeight: 1.6,
            textAlign: 'center'
          }}>
            <strong>이전 버전</strong> - 2025년 1월 15일 ~ 2025년 12월 17일 적용된 방침입니다.<br/>
            <a href="/privacy-policy" style={{ color: '#0284c7' }}>현재 적용 중인 방침 보기</a>
          </p>
        </div>

        <div style={{
          backgroundColor: '#f0f4ff',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          border: '1px solid #c7d2fe'
        }}>
          <p style={{
            fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
            color: '#3730a3',
            margin: 0,
            textAlign: 'center',
            fontWeight: '600'
          }}>
            시행일: 2025년 1월 15일 | 최종 개정일: 2025년 1월 15일
          </p>
        </div>

        <Section title="제1조 (개인정보 수집 항목 및 처리목적)">
          <div style={{
            backgroundColor: '#f0f4ff',
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            border: '1px solid #c7d2fe'
          }}>
            <h4 style={{ color: '#3730a3', marginBottom: '1rem', fontSize: '1.1rem' }}>수집하는 개인정보 항목</h4>
            <ul style={{ paddingLeft: '1.5rem', margin: 0, color: '#4c1d95' }}>
              <li style={{ marginBottom: '0.5rem' }}><strong>필수항목:</strong> 구글 계정 정보(이메일, 이름, 프로필 사진)</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>자동수집:</strong> 서비스 이용기록, 접속 IP, 쿠키, 접속일시</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>결제정보:</strong> 결제수단 정보(토스페이먼츠를 통해 처리)</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>사용자 입력:</strong> AI 서비스 이용 시 입력하는 텍스트 정보</li>
            </ul>
          </div>
          
          <p>플랙티컬 주식회사('왓퀴즈')는 다음의 목적을 위하여 개인정보를 처리합니다. 처리하고 있는 개인정보는 다음의 목적 이외의 용도로는 이용되지 않으며, 이용 목적이 변경되는 경우에는 개인정보보호법 제18조에 따라 별도의 동의를 받는 등 필요한 조치를 이행할 예정입니다.</p>
          
          <ol style={{ paddingLeft: '1.5rem', marginTop: '1rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>회원 가입 및 관리</li>
            <li style={{ marginBottom: '0.5rem' }}>AI 서비스 제공</li>
            <li style={{ marginBottom: '0.5rem' }}>결제 및 정산</li>
            <li style={{ marginBottom: '0.5rem' }}>고객 지원</li>
            <li style={{ marginBottom: '0.5rem' }}>서비스 개선</li>
          </ol>
        </Section>

        <Section title="제2조 (개인정보의 처리 및 보유기간)">
          <p>① 회사는 법령에 따른 개인정보 보유·이용기간 또는 정보주체로부터 개인정보를 수집 시에 동의받은 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.</p>
          
          <div style={{ marginTop: '1rem' }}>
            <h4 style={{ color: '#2d3748', marginBottom: '0.5rem' }}>② 각각의 개인정보 처리 및 보유 기간:</h4>
            <ul style={{ paddingLeft: '1.5rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>회원정보: 회원탈퇴 후 1년</li>
              <li style={{ marginBottom: '0.5rem' }}>결제정보: 전자상거래법에 따라 5년</li>
              <li style={{ marginBottom: '0.5rem' }}>서비스 이용기록: 회원탈퇴 후 1년</li>
              <li style={{ marginBottom: '0.5rem' }}>고객 문의 기록: 문의 완료 후 3년</li>
            </ul>
          </div>
        </Section>

        <Section title="제3조 (개인정보의 제3자 제공)">
          <div style={{
            backgroundColor: '#f0fdf4',
            padding: '1.5rem',
            borderRadius: '12px',
            marginTop: '1rem',
            border: '1px solid #bbf7d0'
          }}>
            <h4 style={{ color: '#166534', marginBottom: '1rem', fontSize: '1.1rem' }}>개인정보 제3자 제공 없음</h4>
            <p style={{ margin: 0, fontSize: '14px', color: '#166534', lineHeight: 1.6 }}>
              회사는 개인정보를 제3자에게 제공하지 않습니다.
            </p>
          </div>
        </Section>

        <Section title="제4조 (개인정보처리의 위탁)">
          <p>① 회사는 원활한 개인정보 업무처리를 위하여 다음과 같이 개인정보 처리업무를 위탁하고 있습니다:</p>
          
          <div style={{
            backgroundColor: '#f7fafc',
            padding: '1.5rem',
            borderRadius: '8px',
            marginTop: '1rem',
            border: '1px solid #e2e8f0'
          }}>
            <ul style={{ paddingLeft: '1.5rem', margin: 0, fontSize: '14px', color: '#4a5568' }}>
              <li style={{ marginBottom: '0.5rem' }}><strong>위탁업체:</strong> 토스페이먼츠(Toss Payments)</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>위탁업무:</strong> 결제 처리, 구독 관리</li>
            </ul>
          </div>
        </Section>

        <Section title="제5조 (정보주체의 권리·의무 및 행사방법)">
          <p>① 정보주체는 회사에 대해 언제든지 다음 각 호의 개인정보 보호 관련 권리를 행사할 수 있습니다:</p>
          
          <ol style={{ paddingLeft: '1.5rem', marginTop: '1rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>개인정보 열람요구</li>
            <li style={{ marginBottom: '0.5rem' }}>개인정보 정정·삭제요구</li>
            <li style={{ marginBottom: '0.5rem' }}>개인정보 처리정지요구</li>
          </ol>
        </Section>

        <Section title="제6조 (개인정보의 안전성 확보조치)">
          <p>회사는 개인정보보호법 제29조에 따라 다음과 같이 안전성 확보에 필요한 기술적/관리적 및 물리적 조치를 하고 있습니다.</p>
          <ul style={{ paddingLeft: '1.5rem', marginTop: '1rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>데이터 암호화</li>
            <li style={{ marginBottom: '0.5rem' }}>접근 권한 관리</li>
            <li style={{ marginBottom: '0.5rem' }}>보안 모니터링</li>
          </ul>
        </Section>

        <Section title="제7조 (개인정보보호책임자)">
          <div style={{
            backgroundColor: '#f0fff4',
            padding: '1.5rem',
            borderRadius: '12px',
            border: '1px solid #9ae6b4'
          }}>
            <p style={{ margin: 0, marginBottom: '1rem', fontWeight: '600', color: '#2f855a' }}>
              개인정보보호책임자 연락처
            </p>
            <ul style={{ paddingLeft: '1.5rem', margin: 0 }}>
              <li style={{ marginBottom: '0.5rem' }}>성명: 강연구</li>
              <li style={{ marginBottom: '0.5rem' }}>직책: 대표이사</li>
              <li style={{ marginBottom: '0.5rem' }}>이메일: angel@plactical.com</li>
              <li style={{ marginBottom: '0.5rem' }}>전화: 02-949-9158</li>
            </ul>
          </div>
        </Section>

        <Section title="제8조 (개인정보처리방침 변경)">
          <p>① 이 개인정보처리방침은 2025년 1월 15일부터 적용됩니다.</p>
          <p>② 이전의 개인정보처리방침은 아래에서 확인하실 수 있습니다.</p>
        </Section>

        <PolicyFooter />
      </div>
    </div>
  );
};

// 공통 컴포넌트
const Section = ({ title, children }) => (
  <section style={{ marginBottom: '2.5rem' }}>
    <h2 style={{
      fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
      fontWeight: '600',
      color: '#2d3748',
      marginBottom: '1rem',
      borderBottom: '2px solid #3182f6',
      paddingBottom: '0.5rem'
    }}>
      {title}
    </h2>
    <div style={{
      fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
      color: '#4a5568'
    }}>
      {children}
    </div>
  </section>
);

const PolicyFooter = () => (
  <div style={{
    marginTop: '3rem',
    padding: '2rem 0',
    borderTop: '1px solid #e2e8f0',
    textAlign: 'center'
  }}>
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      gap: '2rem',
      flexWrap: 'wrap'
    }}>
      <a href="/company-info" style={{ color: '#3182f6', textDecoration: 'none', fontWeight: '500' }}>회사정보</a>
      <a href="/terms-of-service" style={{ color: '#3182f6', textDecoration: 'none', fontWeight: '500' }}>이용약관</a>
      <a href="/refund-policy" style={{ color: '#3182f6', textDecoration: 'none', fontWeight: '500' }}>환불정책</a>
    </div>
  </div>
);

export default PrivacyPolicy;
